<template>
  <v-card v-if="loading" class="online-status__loading" flat>
    <h2 class="text-center">
      <v-progress-circular
          class="mr-2"
          color="primary"
          indeterminate
      ></v-progress-circular
      >
      Loading...
    </h2>
  </v-card>
  <v-card v-else class="online-status" flat>
    <div class="online-status__hero">
      <div class="online-status__hero-text">
        <div class="online-status__title">Online Status</div>
        <div class="online-status__subtitle">Monitor recent activity by last login window.</div>
      </div>
      <div class="online-status__hero-actions">
        <v-autocomplete
            v-model="facilityTypeValue"
            :clearable="clearable"
            :items="FACILITY_TYPES"
            :loading="loading"
            class="online-status__filter"
            dense
            hide-details
            item-text="name"
            item-value="id"
            label="Facility Type"
            outlined
            prepend-inner-icon="mdi-domain"
        ></v-autocomplete>
        <v-autocomplete
            v-if="selectedFacilityTypeCode === 'ACCF'"
            v-model="accreditorValue"
            :clearable="clearable"
            :items="ACCREDITORS"
            :loading="loading"
            class="online-status__filter"
            dense
            hide-details
            item-text="name"
            item-value="id"
            label="Accreditor"
            outlined
            prepend-inner-icon="mdi-account-tie"
        ></v-autocomplete>
        <v-autocomplete
            v-if="selectedFacilityTypeCode === 'CPDPF'"
            v-model="cpdProviderValue"
            :clearable="clearable"
            :items="CPD_PROVIDERS"
            :loading="loading"
            class="online-status__filter"
            dense
            hide-details
            item-text="name"
            item-value="id"
            label="CPD Provider"
            outlined
            prepend-inner-icon="mdi-school"
        ></v-autocomplete>
        <v-autocomplete
            v-if="selectedFacilityTypeCode === 'EINST'"
            v-model="educationalInstitutionValue"
            :clearable="clearable"
            :items="EDUCATIONAL_INSTITUTIONS"
            :loading="loading"
            class="online-status__filter"
            dense
            hide-details
            item-text="name"
            item-value="id"
            label="Educational Institution"
            outlined
            prepend-inner-icon="mdi-domain"
        ></v-autocomplete>
        <v-menu
            ref="startMenu"
            v-model="startMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="240px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
                v-model="startDate"
                label="Start Date"
                prepend-inner-icon="mdi-calendar"
                readonly
                clearable
                outlined
                dense
                hide-details
                class="online-status__filter"
                v-bind="attrs"
                v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
              v-model="startDate"
              no-title
              scrollable
              @input="startMenu = false"
          ></v-date-picker>
        </v-menu>
        <v-menu
            ref="endMenu"
            v-model="endMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="240px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
                v-model="endDate"
                label="End Date"
                prepend-inner-icon="mdi-calendar"
                readonly
                clearable
                outlined
                dense
                hide-details
                class="online-status__filter"
                v-bind="attrs"
                v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
              v-model="endDate"
              no-title
              scrollable
              @input="endMenu = false"
          ></v-date-picker>
        </v-menu>
        <v-autocomplete
            v-model="yearValue"
            :items="availableYears"
            :clearable="clearable"
            :loading="loading"
            class="online-status__filter"
            dense
            hide-details
            label="Year"
            outlined
            prepend-inner-icon="mdi-calendar-range"
        ></v-autocomplete>
      </div>
    </div>
    <v-row class="online-status__grid" dense>
      <v-col class="online-status__col" cols="12" md="5">
        <v-card class="online-status__panel" flat>
          <div class="online-status__panel-header">
            <div class="online-status__panel-title">Activity Windows</div>
            <v-chip class="online-status__panel-chip" color="primary" outlined small>
              {{ totalOnline }} total
            </v-chip>
          </div>
          <v-row class="online-status__summary" dense>
            <v-col
                v-for="(bucket, index) in statusBuckets"
                :key="bucket.label"
                cols="6"
                sm="3"
            >
              <div
                  class="online-status__summary-card"
                  :class="{ 'online-status__summary-card--active': selected === index }"
                  :aria-pressed="selected === index"
                  role="button"
                  tabindex="0"
                  @click="selectBucket(index)"
                  @keydown.enter.prevent="selectBucket(index)"
                  @keydown.space.prevent="selectBucket(index)"
              >
                <span class="online-status__dot" :style="{ backgroundColor: bucket.color }"></span>
                <div class="online-status__summary-count">{{ bucket.value }}</div>
                <div class="online-status__summary-label">{{ bucket.label }}</div>
              </div>
            </v-col>
          </v-row>
          <apexchart
              :options="chartOptions"
              :series="OnlineStatusChart"
              class="online-status__chart"
              height="320"
              type="donut"
              @dataPointSelection="clickHandler"
          ></apexchart>
        </v-card>
      </v-col>
      <v-col class="online-status__col" cols="12" md="7">
        <v-card class="online-status__panel online-status__panel--table" flat>
          <div class="online-status__panel-header">
            <div class="online-status__panel-title">Recent Logins</div>
            <v-chip class="online-status__panel-chip" color="primary" outlined small>
              {{ OnlineStatusTable.length }} users
            </v-chip>
          </div>
          <v-data-table
              :headers="headers"
              :items="OnlineStatusTable"
              :loading="loading"
              class="online-status__table elevation-0"
              dense
              fixed-header
              height="420"
              loading-text="Loading... please wait">
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import {OnlineStatusService} from "@/components/OnlineStatus/onlineStatus.service";
import {TrainingCenterService} from "@/components/TrainingCenter/training_center.service";
import {getAccreditedBy, getFacilityTypes, getUniversities} from "@/utils/helper";

export default {
  name: "VueChart",
  components: {},
  data() {
    return {
      OnlineStatusChart: [],
      OnlineStatusTotals: [],
      OnlineStatusTable: [],
      headers: [
        {text: "Name", value: "name"},
        {text: "Last Login", value: "lastLoginDate"},
        {text: "Sector", value: "sector"}
      ],
      loading: false,
      chartOptions: {
        chart: {
          type: "donut",
          toolbar: {
            show: false,
          },
        },
        colors: ["#2FBF71", "#F6C945", "#F38B4A", "#E63946"],
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "12px",
            fontWeight: "600",
          },
        },
        legend: {
          position: "bottom",
          fontSize: "13px",
          markers: {
            width: 10,
            height: 10,
            radius: 10,
          },
          itemMargin: {
            horizontal: 10,
            vertical: 6,
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                name: {
                  fontSize: "13px",
                  fontWeight: 600,
                },
                value: {
                  fontSize: "20px",
                  fontWeight: 700,
                },
                total: {
                  show: true,
                  label: "Total",
                  fontSize: "12px",
                  fontWeight: 600,
                  formatter: function (w) {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  },
                },
              },
            },
          },
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              height: 240,
            },
            legend: {
              position: "bottom",
            },
          },
        }],
        labels: ['0-7 days', '8-14 days', '15-30 days', '>30 days'],
        stroke: {
          width: 2,
          colors: ["#ffffff"],
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " users";
            },
          },
        },
      },

      FACILITY_TYPES: [],
      ACCREDITORS: [],
      CPD_PROVIDERS: [],
      EDUCATIONAL_INSTITUTIONS: [],
      clearable: true,
      facilityTypeValue: null,
      accreditorValue: null,
      cpdProviderValue: null,
      educationalInstitutionValue: null,
      startDate: null,
      endDate: null,
      yearValue: null,
      startMenu: false,
      endMenu: false,
      suspendFetch: false,
      selected: null,
      allData: []
    };
  },
  computed: {
    statusBuckets() {
      const counts = this.OnlineStatusTotals || [];
      return [
        {label: "0-7 days", value: counts[0] || 0, color: "#2FBF71"},
        {label: "8-14 days", value: counts[1] || 0, color: "#F6C945"},
        {label: "15-30 days", value: counts[2] || 0, color: "#F38B4A"},
        {label: ">30 days", value: counts[3] || 0, color: "#E63946"},
      ];
    },
    totalOnline() {
      return this.OnlineStatusTotals.reduce((sum, value) => sum + value, 0);
    },
    selectedFacilityType() {
      return this.FACILITY_TYPES.find((item) => item.id === this.facilityTypeValue) || null;
    },
    selectedFacilityTypeCode() {
      return this.selectedFacilityType ? this.selectedFacilityType.code : null;
    },
    availableYears() {
      const years = new Set();
      this.allData.forEach(item => {
        const year = new Date(item.lastLoginDate).getFullYear();
        if (!Number.isNaN(year)) {
          years.add(year);
        }
      });
      return Array.from(years).sort((a, b) => b - a);
    },
  },
  watch: {
    facilityTypeValue: {
      handler: function () {
        this.suspendFetch = true;
        this.accreditorValue = null;
        this.cpdProviderValue = null;
        this.educationalInstitutionValue = null;
        this.$nextTick(() => {
          this.suspendFetch = false;
          this.fetchOnlineStatus();
        });
      },
    },
    accreditorValue: {
      handler: function () {
        if (this.suspendFetch) return;
        this.fetchOnlineStatus();
      },
    },
    cpdProviderValue: {
      handler: function () {
        if (this.suspendFetch) return;
        this.fetchOnlineStatus();
      },
    },
    educationalInstitutionValue: {
      handler: function () {
        if (this.suspendFetch) return;
        this.fetchOnlineStatus();
      },
    },
    startDate: {
      handler: function () {
        if (this.suspendFetch) return;
        this.fetchOnlineStatus();
      },
    },
    endDate: {
      handler: function () {
        if (this.suspendFetch) return;
        this.fetchOnlineStatus();
      },
    },
    yearValue: {
      handler: function () {
        if (this.suspendFetch) return;
        this.fetchOnlineStatus();
      },
    },
  },
  async created() {
    await this.fetchOnlineStatus();
    await this.initateLookups();
  },
  methods: {
    normalizeList(list) {
      if (Array.isArray(list)) return list;
      if (Array.isArray(list?.data)) return list.data;
      if (Array.isArray(list?.data?.data)) return list.data.data;
      if (Array.isArray(list?.data?.rows)) return list.data.rows;
      if (Array.isArray(list?.rows)) return list.rows;
      return [];
    },
    normalizeNamedList(list, nameKey) {
      return this.normalizeList(list).map((item) => ({
        ...item,
        name: item.name || item[nameKey],
      }));
    },
    buildSearchParams() {
      const params = new URLSearchParams();
      if (this.facilityTypeValue !== null) {
        params.append("facilityTypeId", this.facilityTypeValue);
      }
      if (this.accreditorValue !== null) {
        params.append("accreditorId", this.accreditorValue);
      }
      if (this.cpdProviderValue !== null) {
        params.append("cpdProviderId", this.cpdProviderValue);
      }
      if (this.educationalInstitutionValue !== null) {
        params.append("eduInstitutionId", this.educationalInstitutionValue);
      }
      if (this.startDate) {
        params.append("startDate", this.startDate);
      }
      if (this.endDate) {
        params.append("endDate", this.endDate);
      }
      if (this.yearValue) {
        params.append("year", this.yearValue);
      }
      const query = params.toString();
      return query ? `?${query}` : "";
    },
    async fetchOnlineStatus() {
      this.loading = true;
      const searchParams = this.buildSearchParams();
      const onlineStatusResp = await OnlineStatusService.getOnlineStatus(searchParams);
      this.allData = this.normalizeList(onlineStatusResp);
      this.OnlineStatusTotals = this.buildBuckets(this.allData);
      this.selected = null;
      this.applyData(this.allData);
      this.loading = false;
    },
    async initateLookups() {
      this.loading = true;
      const [facilityTypes, accreditors, cpdProviders, universities] = await Promise.all([
        getFacilityTypes(),
        getAccreditedBy(),
        TrainingCenterService.getTrainingCentersDrp(),
        getUniversities(),
      ]);
      this.FACILITY_TYPES = this.normalizeNamedList(facilityTypes, "name")
          .sort((a, b) => a.name.localeCompare(b.name));
      this.ACCREDITORS = this.normalizeNamedList(accreditors, "accreditorName")
          .sort((a, b) => a.name.localeCompare(b.name));
      this.CPD_PROVIDERS = this.normalizeNamedList(cpdProviders, "trainingCenterName")
          .sort((a, b) => a.name.localeCompare(b.name));
      this.EDUCATIONAL_INSTITUTIONS = this.normalizeNamedList(universities, "name")
          .filter((data) => data.isActive === true)
          .sort((a, b) => a.name.localeCompare(b.name));
      this.loading = false;
    },
    clickHandler(event, chartContext, config) {
      if (config && Number.isInteger(config.dataPointIndex) && config.dataPointIndex >= 0) {
        this.selectBucket(config.dataPointIndex);
      }
    },
    getDiffDays(item) {
      const today = new Date(new Date().toISOString().split('T')[0]);
      const lastLoginDate = new Date(item.lastLoginDate);
      return Math.abs(today - lastLoginDate) / (24 * 60 * 60 * 1000);
    },
    getBucketIndex(diffTime) {
      if (diffTime < 7) {
        return 0;
      }
      if (diffTime < 14) {
        return 1;
      }
      if (diffTime < 30) {
        return 2;
      }
      return 3;
    },
    buildBuckets(data) {
      const buckets = [0, 0, 0, 0];
      (data || []).forEach(item => {
        const diffTime = this.getDiffDays(item);
        const bucketIndex = this.getBucketIndex(diffTime);
        buckets[bucketIndex] += 1;
      });
      return buckets;
    },
    applyData(data) {
      this.OnlineStatusTable = data;
      this.OnlineStatusChart = this.buildBuckets(data);
    },
    selectBucket(index) {
      if (this.selected === index) {
        this.selected = null;
        this.applyData(this.allData);
        return;
      }
      this.selected = index;
      const filtered = this.allData.filter(item => {
        const diffTime = this.getDiffDays(item);
        return this.getBucketIndex(diffTime) === index;
      });
      this.applyData(filtered);
    },
  },
};
</script>

<style scoped>
.online-status {
  min-height: 100vh;
  padding: 24px;
  background: linear-gradient(180deg, #f7f9fc 0%, #eef3f8 100%);
  border-radius: 0;
}

.online-status__loading {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f9fc;
}

.online-status__hero {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  margin-bottom: 20px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.online-status__title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.online-status__subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: #6b7280;
}

.online-status__hero-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  min-width: 240px;
}

.online-status__filter {
  flex: 1 1 200px;
  max-width: 240px;
  min-width: 180px;
}

.online-status__grid {
  margin-top: 4px;
}

.online-status__panel {
  width: 100%;
  height: 100%;
  padding: 20px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.06);
}

.online-status__panel--table {
  height: 100%;
}

.online-status__col {
  display: flex;
}

.online-status__panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.online-status__panel-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.online-status__panel-chip {
  text-transform: none;
  letter-spacing: 0;
}

.online-status__summary {
  margin-bottom: 8px;
}

.online-status__summary-card {
  display: grid;
  grid-template-columns: 12px 1fr;
  grid-template-rows: auto auto;
  gap: 4px 8px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #edf2f7;
  background: #f8fafc;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.online-status__summary-card--active {
  border-color: #2563eb;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.15);
  transform: translateY(-1px);
}

.online-status__summary-card:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.online-status__dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  margin-top: 4px;
  grid-row: 1 / span 2;
}

.online-status__summary-count {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.1;
}

.online-status__summary-label {
  font-size: 11px;
  color: #6b7280;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.online-status__chart {
  margin-top: 6px;
}

.online-status__table {
  border-radius: 12px;
  overflow: hidden;
}

.online-status__table ::v-deep thead th {
  background: #f3f6fb;
  color: #111827;
  font-weight: 600;
}

.online-status__table ::v-deep tbody tr:hover {
  background: #f9fbff;
}

.online-status__filter ::v-deep .v-input__slot {
  border-radius: 12px;
}
</style>
